import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import {
  ContextService,
  SessionService,
  TokenService,
  parseServerSentEvents,
} from '@maximizer/core/shared/data-access';
import { Octopus, OpenAI } from '@maximizer/core/shared/domain';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { Observable, firstValueFrom, map, switchMap } from 'rxjs';
import {
  INTEGRATION_WEB_APP_URL,
  INTEGRATION_SUBSCRIPTION_KEY,
  INTEGRATION_IQ_BOOST_URL,
} from '../types/injection-token';
import {
  AnonymizeRequest,
  AnonymizeResponseData,
} from '../classes/pii-anonymization.types';
import { StreamingPhase } from '../classes/iqBoost';
import { IqBoostPromptCategory } from '../types/iqboost-prompts.types';
import {
  IqBoostPromptLookupRequest,
  IqBoostPromptLookupResult,
} from '../types/iqboost-prompt-lookup.types';

export interface IqBoostResponseChunk extends OpenAI.StreamRequestChunk {
  phase?: StreamingPhase;
}

const STREAMING_PHASE_EVENT = 'phase';

/** Maps server `phase` / `phase:step` to StreamingPhase. */
const STREAMING_PHASES: Record<string, StreamingPhase | undefined> = {
  gettingContext: StreamingPhase.GettingContext,
  'gettingContext:readingRecords': StreamingPhase.GettingContextReadingRecords,
  'gettingContext:readingDocuments': StreamingPhase.GettingContextReadingDocuments,
  'gettingContext:findingRelevant': StreamingPhase.GettingContextFindingRelevant,
  processingCustomDataSources: StreamingPhase.ProcessingCustomDataSources,
  anonymizing: StreamingPhase.Anonymizing,
  thinking: StreamingPhase.Thinking,
  webSearch: StreamingPhase.WebSearch,
};

/** Extra messages appended to the prompt resolved server-side from `promptName`. */
export interface IqBoostAdditionalMessages {
  user?: string[];
  system?: string[];
}

export interface GenerateIqBoostRequest {
  entityKey: string;
  promptName: string;
  entityType?: string;
  conversationId?: string;
  userPrompt?: string;
  language?: string;
  additionalMessages?: IqBoostAdditionalMessages;
  /** Regenerate: bypass the server-side response cache so the user gets a fresh answer. */
  invalidateCache?: boolean;
}

/** Sent only when invalidating; its absence is what tells the server to serve from cache. */
const INVALIDATE_CACHE_HEADER = 'X-Invalidate-Cache';

interface GenerateIqBoostResponseRaw extends Octopus.Response {
  Data?: {
    Content: string;
    Model: string;
    Usage: OpenAI.CompletionResponse['usage'];
    ConversationId: string | null;
    Cached: boolean;
  };
}

export interface GetIqBoostPromptsRequest {
  language?: string;
  type?: 'single' | 'all';
}

interface GetIqBoostPromptsResponseRaw extends Octopus.Response {
  Data?: IqBoostPromptCategory[];
}

interface LookupIqBoostPromptsResponseRaw extends Octopus.Response {
  Data?: IqBoostPromptLookupResult[];
}

@Injectable()
export class MaximizerAiService {

  get baseUrl(): string {
    return `${this.integrationWebAppUrl}${this.integrationIqBoostUrl}`;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly session: SessionService,
    private readonly context: ContextService,
    private readonly token: TokenService,
    @Inject(INTEGRATION_IQ_BOOST_URL) private readonly integrationIqBoostUrl: string,
    @Inject(INTEGRATION_WEB_APP_URL) private readonly integrationWebAppUrl: string,
    @Inject(INTEGRATION_SUBSCRIPTION_KEY) private readonly integrationSubscriptionKey: string,
  ) {}

  generateResponse(
    request: GenerateIqBoostRequest,
  ): Observable<IqBoostResponseChunk> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.streamWithAuthRetry(
          this.toRequestBody(request),
          request.invalidateCache ?? false,
        ),
      ),
    );
  }

  private streamWithAuthRetry(
    body: unknown,
    invalidateCache: boolean,
  ): Observable<IqBoostResponseChunk> {
    return new Observable<IqBoostResponseChunk>((subscriber) => {
      const controller = new AbortController();

      const open = async (bearer: string): Promise<Response> =>
        fetch(`${this.baseUrl}/generate-response`, {
          method: 'POST',
          headers: {
            ...this.getHeaders(),
            Authorization: `Bearer ${bearer}`,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream, application/json',
            ...(invalidateCache ? { [INVALIDATE_CACHE_HEADER]: 'true' } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

      const consume = async (): Promise<void> => {
        let response = await open(this.context.token || '');

        // Pre-stream 401s arrive as status codes; refresh once and retry.
        if (response.status === 401) {
          this.context.token = '';
          const refreshed = await firstValueFrom(this.token.getToken());
          if (isNullOrEmpty(refreshed)) {
            throw new Error('IQ Boost streaming failed: unable to refresh the token');
          }
          response = await open(refreshed);
        }

        if (!response.ok) {
          throw new Error(await this.readStreamError(response));
        }

        const contentType = response.headers.get('Content-Type') ?? '';
        if (!contentType.includes('text/event-stream')) {
          subscriber.next(await this.readBufferedResponse(response));
          return;
        }
        if (!response.body) {
          throw new Error('IQ Boost stream response did not include a body');
        }

        for await (const chunk of parseServerSentEvents(response.body)) {
          const failure = this.tryReadStreamErrorEvent(chunk);
          if (failure) {
            throw new Error(failure);
          }

          const phase = this.tryReadPhaseEvent(chunk);
          if (phase !== undefined) {
            subscriber.next({ phase });
            continue;
          }

          const completion = chunk as OpenAI.CompletionChunk;
          const content = completion.choices?.[0]?.delta?.content;
          const usage = completion.usage;
          const conversationId = completion.conversation_id;
          if (content || usage || conversationId) {
            subscriber.next({ content, usage, conversationId });
          }
        }
      };

      consume()
        .then(() => subscriber.complete())
        .catch((error: unknown) => {
          // Ignore AbortError from our own teardown.
          if (!controller.signal.aborted) {
            subscriber.error(error);
          }
        });

      return () => controller.abort();
    });
  }

  private async readStreamError(response: Response): Promise<string> {
    const body = await response.text();
    const parsed = this.tryReadStreamErrorEvent(this.tryParseJson(body));
    const trimmed = body.trim();
    return parsed ?? (trimmed !== '' ? trimmed : `IQ Boost stream request failed with status ${response.status}`);
  }

  private tryReadPhaseEvent(payload: unknown): StreamingPhase | undefined {
    const event = payload as { type?: string; phase?: string; step?: string } | null | undefined;
    if (event?.type !== STREAMING_PHASE_EVENT || !event.phase) {
      return undefined;
    }

    // Unknown sub-steps fall back to the parent phase.
    const step = event.step ? STREAMING_PHASES[`${event.phase}:${event.step}`] : undefined;
    return step ?? STREAMING_PHASES[event.phase];
  }

  private tryReadStreamErrorEvent(payload: unknown): string | null {
    const envelope = payload as
      | { Data?: { type?: string }; Msg?: Array<{ Message?: string } | string> }
      | null
      | undefined;

    if (envelope?.Data?.type !== 'error') {
      return null;
    }

    const first = envelope.Msg?.[0];
    const message = typeof first === 'string' ? first : first?.Message;
    return message ?? 'IQ Boost stream failed';
  }

  private tryParseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private toRequestBody(request: GenerateIqBoostRequest) {
    return {
      entityKey: request.entityKey,
      promptName: request.promptName,
      entityType: request.entityType ?? 'addressBook',
      conversationId: request.conversationId,
      userPrompt: request.userPrompt,
      language: request.language ?? 'en',
      additionalMessages: request.additionalMessages,
    };
  }

  /** Folds a buffered JSON envelope into one stream chunk. */
  private async readBufferedResponse(
    response: Response,
  ): Promise<IqBoostResponseChunk> {
    const envelope = this.tryParseJson(await response.text()) as
      | GenerateIqBoostResponseRaw
      | null;

    if (envelope?.Code !== Octopus.ResponseStatusCode.Successful || !envelope.Data) {
      throw new Error('IQ Boost generate-response failed');
    }

    return {
      content: envelope.Data.Content ?? '',
      usage: envelope.Data.Usage,
      conversationId: envelope.Data.ConversationId ?? undefined,
    };
  }

  anonymize(request: AnonymizeRequest): Observable<AnonymizeResponseData> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.post<AnonymizeResponseData>(
          `${this.baseUrl}/anonymize`,
          request,
        ),
      ),
    );
  }

  /** Sidebar prompt catalog; server groups, sorts, and merges custom projects. */
  getPrompts(request: GetIqBoostPromptsRequest): Observable<IqBoostPromptCategory[]> {
    let params = new HttpParams();
    if (request.language) {
      params = params.set('language', request.language);
    }
    if (request.type) {
      params = params.set('type', request.type);
    }

    return this.session.getInfo().pipe(
      switchMap(() =>
        this.get<GetIqBoostPromptsResponseRaw>(
          `${this.baseUrl}/prompts/insights`,
          params,
        ),
      ),
      map((envelope) => this.readPromptData(envelope, 'prompts/insights')),
    );
  }

  /** Resolves prompt bodies server-side so Langfuse credentials stay off the browser. */
  lookupPrompts(
    request: IqBoostPromptLookupRequest,
  ): Observable<IqBoostPromptLookupResult[]> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.post<LookupIqBoostPromptsResponseRaw>(
          `${this.baseUrl}/prompts/lookup`,
          request,
        ),
      ),
      map((envelope) => this.readPromptData(envelope, 'prompts/lookup')),
    );
  }

  /** Callers fall back to bundled prompts on failure, so log the envelope to keep it diagnosable. */
  private readPromptData<T>(
    envelope: Octopus.Response & { Data?: T[] },
    operation: string,
  ): T[] {
    if (envelope.Code !== Octopus.ResponseStatusCode.Successful) {
      console.warn(`IQ Boost ${operation} failed`, envelope.Code, envelope.Msg);
      return [];
    }

    return envelope.Data ?? [];
  }

  // SLE/transcript toggles are resolved server-side; cache invalidation is per-request
  // and travels on the generate-response call only.
  private getHeaders() {
    return {
      'Ocp-Apim-Subscription-Key': this.integrationSubscriptionKey,
    };
  }

  private post<T>(url: string, body: unknown): Observable<T> {
    return this.http.post<T>(url, body, { headers: this.getHeaders() });
  }

  private get<T>(url: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(url, { headers: this.getHeaders(), params });
  }
}
